<template>
  <div>
    <dotted-dropdown
      v-if="showOptions"
      id="page-header-options"
    >
      <li v-if="allowChangePIC">
        <DialogSlot
          id="change-pic-dialog"
          ref="change-pic-dialog"
          :modalBtnClass="'btn--transparent'"
          :modalBtn="$t('action.changePIC')"
          :modalHeader="$t('changePIC')"
          :afterOpenModal="afterOpenModal"
        >
          <div
            slot="content"
            class="pic-slot-content"
          >
            <div>
              <label>{{ $t('currentPIC') }}</label>
              <div>{{ previousPersonInCharge }}</div>
            </div>

            <div>
              <label>{{ $t('newPIC') }}</label>
              <HKLSelect
                :value="newPersonInCharge || undefined"
                :options="picHklOptions"
                :showSearch="true"
                placeholder=""
                @change="newPersonInCharge = $event || ''"
              />
            </div>
          </div>
          <div slot="buttons">
            <button
              type="button"
              class="btn btn--orange close-reveal-modal"
              :disabled="!newPersonInCharge || newPersonInCharge === previousPersonInCharge"
              @click="changePIC"
            >{{ $t('action.ok') }}</button>
          </div>
        </DialogSlot>
      </li>
      <li v-if="showSubstitute">
        <slot name="substitute" />
      </li>
      <li v-if="allowCancel">
        <DialogSlot
          id="page-header-cancel-dialog"
          ref="cancel-dialog"
          :modalBtnClass="'btn--transparent'"
          :modalBtn="cancelOption"
        >
          <div
            slot="content"
            class="slot-content"
          >
            <span class="content-message">{{ confirmCancelMsg }}</span>
          </div>
          <div slot="buttons">
            <button
              type="button"
              class="btn btn--orange close-reveal-modal"
              @click="cancelItem"
            >{{ $t('action.yesContinue') }}</button>
          </div>
        </DialogSlot>
      </li>
    </dotted-dropdown>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DialogSlot from '@/controls/DialogSlot'
import DottedDropdown from '@/controls/DottedDropdown'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import userHelper from '@/utils/userHelper'
import { getLocations, getUsersForSite } from '@/api/locations/request'
import { getUsersByPermsAndBuildings } from '@/api/users/request'
import { PermTypeMapKeys } from '@/utils/aclHelper'

export default {
  name: 'PageHeaderOptions',
  components: {
    DialogSlot,
    HKLSelect,
    DottedDropdown,
  },
  props: {
    allowChangePIC: {
      type: Boolean,
      default: true,
    },
    allowCancel: Boolean,
    showSubstitute: {
      type: Boolean,
      default: false,
    },
    confirmCancelMsg: {
      type: String,
      default: '',
    },
    itemName: {
      type: String,
      default: '',
    },
    site: {
      type: String,
      default: '',
    },
    ignoredSiteUsernames: {
      type: Array,
      default () {
        return []
      },
    },
    getSelectedList: {
      type: Function,
      default: () => false,
    },
    previousPersonInCharge: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      usersList: {},
      newPersonInCharge: '',
    }
  },
  computed: {
    ...mapGetters('projects', ['currentProject']),
    showOptions () {
      return this.allowChangePIC || this.allowCancel || this.showSubstitute
    },
    cancelOption () {
      return `${this.$i18n.t('action.cancel')} ${this.itemName ? this.itemName : ''}`
    },
    picHklOptions () {
      return Object.keys(this.usersList).map((key) => ({ name: key, value: key }))
    },
  },
  methods: {
    async afterOpenModal () {
      const result = await this.getSelectedList()
      this.usersList = result || await this.getUsersForSameSite()
      if (Object.keys(this.usersList).length && this.previousPersonInCharge) {
        delete this.usersList[this.previousPersonInCharge]
      }
    },

    async getUsersForSameSite () {
      this.usersList = {}
      let users
      const isProjectDetail = this.$route.name === 'projectDetail'
      const permTypes = isProjectDetail
        ? PermTypeMapKeys.PROJECT_MANAGE
        : PermTypeMapKeys.TENDER_CREATE_UPDATE
      if (this.site && this.site !== null) {
        let site = isProjectDetail ? this.currentProject.site : null
        site = site || (await getLocations({ code: this.site }))[0]
        users = await getUsersForSite(site.id, { permTypes })
      } else {
        users = await getUsersByPermsAndBuildings(permTypes)
      }
      return users
        .filter((user) => !this.ignoredSiteUsernames.includes(user.username))
        .map((user) => {
          const name = userHelper.getUserName(user.username, user.displayName)
          const displayPicName = `${name} (${user.username})`
          return { ...user, displayPicName }
        })
        .sort((a, b) => a.displayPicName.localeCompare(b.displayPicName))
        .reduce((partialData, user) => {
          const { displayPicName } = user
          partialData[displayPicName] = user
          return partialData
        }, {})
    },

    changePIC () {
      this.$emit('change-pic', this.usersList[this.newPersonInCharge])
      this.$refs['change-pic-dialog'].closeModal()
    },
    cancelItem () {
      this.$emit('cancelItem')
      this.$refs['cancel-dialog'].closeModal()
    },
  },
}
</script>

<style lang="scss">
#change-pic-dialog {
  .pic-slot-content {
    label {
      font-size: 14px;
      font-weight: bold;
      color: #0C3A58;
    }
    & > div:last-child {
      margin-top: 15px;
    }
  }
}

#page-header-cancel-dialog {
  font-size: 14px;
  color: #0c3a58;
  font-weight: bold;
  .close-btn {
    font-weight: normal;
  }
}
</style>

<style scoped>
  .content-message{
      font-weight: bold;
  }

</style>
